import { Calendar } from '@/design-system/components/dateRangePicker/types';
import moment, { Moment } from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';

interface UseDatePickerSelectorParams {
  isOpen?: boolean;
  value: Moment | null;
  onValueChange?: (value: Moment | null) => void;
}

export const useDatePickerSelector = (params: UseDatePickerSelectorParams) => {
  const { isOpen, value, onValueChange } = params;
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);

  const [filter, setFilter] = useState<{
    month: number;
    year: number;
  }>({
    month: moment().month() + 1,
    year: moment().year()
  });

  const calendarLeft = useMemo(() => {
    return new Calendar({
      month: filter.month,
      year: filter.year
    });
  }, [filter]);

  const calendarRight = useMemo(() => {
    return new Calendar({
      month: filter.month + 1,
      year: filter.year
    });
  }, [filter]);

  useEffect(() => {
    onValueChange && onValueChange(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (isOpen) {
      setSelectedDate(value);
    }
  }, [isOpen]);

  const onPreviousMonth = () => {
    if (filter.month === 1) {
      setFilter({
        month: 12,
        year: filter.year - 1
      });
    } else {
      setFilter({
        month: filter.month - 1,
        year: filter.year
      });
    }
  };

  const onNextMonth = () => {
    if (filter.month === 12) {
      setFilter({
        month: 1,
        year: filter.year + 1
      });
    } else {
      setFilter({
        month: filter.month + 1,
        year: filter.year
      });
    }
  };

  const onNextYear = () => {
    setFilter({
      month: filter.month,
      year: filter.year + 1
    });
  };

  const onYear = (year: number) => {
    setFilter({
      month: filter.month,
      year: year
    });
  };

  const onPreviousYear = () => {
    setFilter({
      month: filter.month,
      year: filter.year - 1
    });
  };

  const onReset = () => {
    setSelectedDate(null);
  };

  const onSelectDate = (date: Moment | null) => {
    if (!date) return;
    setSelectedDate(date);
  };

  return {
    month: filter.month,
    year: filter.year,
    calendarLeft: calendarLeft.getCalendar(),
    calendarRight: calendarRight.getCalendar(),
    selectedDate: selectedDate,
    onSelectDate,
    onPreviousMonth,
    onNextMonth,
    onPreviousYear,
    onNextYear,
    onReset,
    onYear
  };
};
